import { useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const REASONS = [
  { id: "spam", label: "Spam or self-promo" },
  { id: "harassment", label: "Harassment or personal attack" },
  { id: "off-topic", label: "Off-topic / not about the server" },
  { id: "inappropriate", label: "NSFW / hateful content" },
  { id: "other", label: "Something else" },
];

/**
 * "Report this review" dialog. Fires POST /api/reviews/:id/report which
 * lands in the same staff queue as server reports (tagged target=review).
 */
export default function ReviewReportDialog({ review }) {
  const { user, loginWithDiscord } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!user) {
      loginWithDiscord();
      return;
    }
    if (!reason) return toast.error("Pick a reason");
    setSubmitting(true);
    try {
      await api.post(`/reviews/${review.id}/report`, {
        reason,
        details: details.trim(),
      });
      toast.success("Report sent — staff will review it.");
      setOpen(false);
      setReason("");
      setDetails("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`report-review-btn-${review.id}`}
          title="Report this review"
          className="h-8 w-8 rounded-lg bg-transparent hover:bg-red-500/10 border border-white/10 hover:border-red-500/40 text-zinc-500 hover:text-red-400 flex items-center justify-center transition-colors shrink-0"
        >
          <Flag className="w-3.5 h-3.5" />
        </button>
      </DialogTrigger>

      <DialogContent
        className="bg-[#0A0A0F] border-white/10 text-white max-w-md"
        data-testid="review-report-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">Report this review</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Staff will look at it. Only report reviews that genuinely break the rules — false reports may cost you the ability to report again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
              Reason
            </div>
            <div className="grid grid-cols-1 gap-2">
              {REASONS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setReason(r.id)}
                  data-testid={`review-report-reason-${r.id}`}
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    reason === r.id
                      ? "border-red-500/60 bg-red-500/10 text-red-100"
                      : "border-white/10 bg-[#12121A] text-zinc-300 hover:border-white/20"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
              Extra details (optional)
            </div>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value.slice(0, 1000))}
              placeholder="Anything staff should know?"
              rows={3}
              data-testid="review-report-details"
              className="w-full bg-[#12121A] border border-white/10 rounded-lg px-3 py-2 text-sm placeholder-zinc-500 focus:border-red-500/60 focus:outline-none focus:ring-1 focus:ring-red-500/40 resize-none"
            />
            <div className="text-[10px] font-mono text-zinc-600 text-right mt-1">
              {details.length}/1000
            </div>
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="h-9 px-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !reason}
            data-testid="review-report-submit"
            className="h-9 px-4 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-100 text-sm font-bold disabled:opacity-40"
          >
            {submitting ? "Sending..." : "Send report"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
